import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Eye, EyeOff, LogIn, Mail } from 'lucide-react';

export default function Login({ onLogin }) {
  const [isOTPMode, setIsOTPMode] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      toast.success('Login successful!');
      onLogin(response.data.user, response.data.access_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/request-otp`, { email });
      toast.success('OTP sent to your email!');
      setOtpSent(true);
      if (response.data.otp) {
        toast.info(`Demo OTP: ${response.data.otp}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, { email, otp });
      toast.success('Login successful!');
      onLogin(response.data.user, response.data.access_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8 animate-slide-in">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Factskoot Invest One
            </h1>
            <p className="text-muted-foreground text-lg">Investor Management Portal</p>
          </div>

          <div className="card p-8">
            <div className="flex gap-2 mb-6">
              <button
                data-testid="password-login-tab"
                onClick={() => {
                  setIsOTPMode(false);
                  setOtpSent(false);
                }}
                className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                  !isOTPMode ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'
                }`}
              >
                <LogIn className="inline w-4 h-4 mr-2" />
                Password
              </button>
              <button
                data-testid="otp-login-tab"
                onClick={() => {
                  setIsOTPMode(true);
                  setOtpSent(false);
                }}
                className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                  isOTPMode ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'
                }`}
              >
                <Mail className="inline w-4 h-4 mr-2" />
                Email OTP
              </button>
            </div>

            {!isOTPMode ? (
              <form onSubmit={handlePasswordLogin} className="space-y-4" data-testid="password-login-form">
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    data-testid="email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="input-field"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Password</label>
                  <div className="relative">
                    <input
                      data-testid="password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="input-field pr-10"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <button
                  data-testid="login-button"
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary"
                >
                  {loading ? 'Logging in...' : 'Login'}
                </button>
              </form>
            ) : (
              <form onSubmit={otpSent ? handleVerifyOTP : handleRequestOTP} className="space-y-4" data-testid="otp-login-form">
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    data-testid="otp-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={otpSent}
                    className="input-field"
                    placeholder="your@email.com"
                  />
                </div>
                {otpSent && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Enter OTP</label>
                    <input
                      data-testid="otp-input"
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      required
                      maxLength={6}
                      className="input-field font-mono text-center text-xl tracking-widest"
                      placeholder="000000"
                    />
                  </div>
                )}
                <button
                  data-testid={otpSent ? 'verify-otp-button' : 'request-otp-button'}
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary"
                >
                  {loading ? 'Please wait...' : otpSent ? 'Verify OTP' : 'Send OTP'}
                </button>
                {otpSent && (
                  <button
                    type="button"
                    onClick={() => setOtpSent(false)}
                    className="w-full btn-outline"
                  >
                    Resend OTP
                  </button>
                )}
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Right side - Image with overlay */}
      <div 
        className="hidden lg:block lg:w-1/2 relative bg-cover bg-center"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1705762893650-786e65257cb8?crop=entropy&cs=srgb&fm=jpg&q=85')` 
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-primary/70 backdrop-blur-sm">
          <div className="h-full flex items-center justify-center p-12">
            <div className="max-w-lg text-center text-white space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Secure Investment Management
              </h2>
              <p className="text-lg opacity-90">
                Track your investments, monitor returns, and manage your portfolio with confidence.
              </p>
              <div className="grid grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold font-mono">99.9%</div>
                  <div className="text-sm opacity-80">Uptime</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold font-mono">24/7</div>
                  <div className="text-sm opacity-80">Access</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold font-mono">Secure</div>
                  <div className="text-sm opacity-80">Encrypted</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}